/**
 * Collects durations for the phases that make up the first app data load.
 *
 * Timings are intentionally kept in module scope: `useUserDataLoader` and
 * `DataLoaderManager` run independently, but both contribute phases to the same
 * "how long did the app take to load" snapshot. `recordInitialLoadTimings`
 * writes that snapshot to `Events` once the shell is usable.
 */

const completedTimings = new Map<string, number>();
const startedTimings = new Map<string, number>();

/** Changes only on a full page load; used for the end-to-end `total` timing. */
const PageLoadStartedAt = performance.now();

export function startLoadTiming(name: string): void {
  startedTimings.set(name, performance.now());
}

export function endLoadTiming(name: string): void {
  const startedAt = startedTimings.get(name);
  if (startedAt == null) return;

  startedTimings.delete(name);
  completedTimings.set(
    name,
    Math.max(0, Math.round(performance.now() - startedAt))
  );
}

export function resetLoadTimings(): void {
  completedTimings.clear();
  startedTimings.clear();
}

export function getLoadTimingsSnapshot(): Record<string, number> {
  const timings: Record<string, number> = {};
  for (const [name, duration] of completedTimings) {
    timings[name] = duration;
  }
  return timings;
}

export function getPageLoadElapsedMs(): number {
  return Math.max(0, Math.round(performance.now() - PageLoadStartedAt));
}
